import React, { useState, useEffect, useCallback } from 'react';
import { CdsContentPanel } from 'clayco-component-library';
import { useMsal } from '@azure/msal-react';
import * as Icon from 'react-bootstrap-icons';
import { Button } from 'react-bootstrap';
import ApiProvider from '../../../apiProvider';
import EmployeeLookup from '../../../Components/EmployeeLookup/EmployeeLookup';

// PayrollAdmin-only management of a payroll page's allow list. Employees added here can see and
// open the page even when their job family / home company does not qualify.
// endpoint: API controller route, e.g. 'staybenefit' or 'householditems'
const PageAccessManager = ({ endpoint, pageName }) => {
  const { instance: msalInstance, accounts: msalAccounts } = useMsal();
  const activeAccount = msalAccounts?.[0];

  const [grants, setGrants] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [lookupKey, setLookupKey] = useState(0); // bumping this resets the typeahead after an add
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toMMDDYYYY = (dt) => {
    if (!dt) return '';
    const match = String(dt).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const [, y, m, d] = match;
    return `${m}/${d}/${y}`;
  };

  const loadGrants = useCallback(async () => {
    if (!msalInstance || !activeAccount) return;
    setLoading(true);
    setError('');
    try {
      const api = new ApiProvider(msalInstance, activeAccount);
      const response = await api.Get(`${endpoint}/access`);
      if (response?.error) throw new Error(response.error);
      setGrants(Array.isArray(response?.items) ? response.items : []);
    } catch {
      setError('Failed to load the access list.');
      setGrants([]);
    } finally {
      setLoading(false);
    }
  }, [msalInstance, activeAccount, endpoint]);

  useEffect(() => { loadGrants(); }, [loadGrants]);

  const handleAdd = async () => {
    if (!selectedEmployee) return;
    if (!msalInstance || !activeAccount) {
      setError('Authentication required.');
      return;
    }
    const employeeNumber = Number(selectedEmployee.employeeNumber);
    if (!employeeNumber) {
      setError('The selected employee has no employee number and cannot be granted access.');
      return;
    }
    if (grants.some(g => Number(g.employeeNumber) === employeeNumber)) {
      setError('That employee already has access.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const api = new ApiProvider(msalInstance, activeAccount);
      const response = await api.Post(`${endpoint}/access`, {
        employeeNumber,
        employeeName: selectedEmployee.displayName,
        employeeEmail: selectedEmployee.email
      });
      if (response?.error) throw new Error(response.error);
      setSelectedEmployee(null);
      setLookupKey(k => k + 1);
      await loadGrants();
    } catch {
      setError('Failed to grant access.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (grant) => {
    if (!window.confirm(`Remove ${pageName} access for ${grant.employeeName || grant.employeeNumber}?`)) return;
    if (!msalInstance || !activeAccount) {
      setError('Authentication required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const api = new ApiProvider(msalInstance, activeAccount);
      const response = await api.Delete(`${endpoint}/access/${grant.employeeNumber}`);
      if (response?.error) throw new Error(response.error);
      setGrants(prev => prev.filter(g => g.employeeNumber !== grant.employeeNumber));
    } catch {
      setError('Failed to remove access.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <CdsContentPanel title={`${pageName} Page Access (Admin)`} collapsible={false}>
        <p className="text-muted">
          Employees in the eligible job families already have access to the {pageName} page. Add anyone
          else who needs it here; they will see the page the next time they open the Employee Portal.
        </p>

        <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
          <div style={{ minWidth: 320, maxWidth: 480, flex: '1 1 320px' }}>
            <EmployeeLookup
              key={`${endpoint}-access-lookup-${lookupKey}`}
              label="Employee"
              name={`${endpoint}AccessEmployee`}
              defaultValue={null}
              onEmployeeSelected={(emp) => { setSelectedEmployee(emp); setError(''); }}
            />
          </div>
          <Button variant="primary" onClick={handleAdd} disabled={!selectedEmployee || saving || loading}>
            {saving ? (
              <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving…</>
            ) : 'Add Employee'}
          </Button>
        </div>

        {error && <div className="text-danger mb-2">{error}</div>}

        {loading ? <div>Loading...</div> : (
          <table className="table table-striped align-middle">
            <thead className="table-light">
              <tr>
                <th scope="col">Employee</th>
                <th scope="col">Email</th>
                <th scope="col">Added By</th>
                <th scope="col">Added On</th>
                <th scope="col" style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {grants.length === 0 ? (
                <tr><td colSpan="5" className="text-muted">No employees have been added.</td></tr>
              ) : grants.map(grant => (
                <tr key={grant.employeeNumber}>
                  <td>{`${grant.employeeName || ''}${grant.employeeNumber ? ' (' + grant.employeeNumber + ')' : ''}`.trim()}</td>
                  <td>{grant.employeeEmail || ''}</td>
                  <td>{grant.grantedByName || ''}</td>
                  <td>{toMMDDYYYY(grant.grantedOn)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
                      style={{ whiteSpace: 'nowrap' }}
                      title={`Remove ${pageName} access`}
                      disabled={saving}
                      onClick={() => handleRemove(grant)}
                    >
                      <Icon.TrashFill /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CdsContentPanel>
    </div>
  );
};

export default PageAccessManager;
